import { create } from 'zustand';
import {
  invalidateCommands,
  onInteractionUpdate,
  onMessageUpdated,
  resetCommands,
} from './commands';
import {
  compareSnowflakes,
  type Channel,
  type GatewayEvents,
  type Guild,
  type Member,
  type Message,
  type NotificationSetting,
  type Presence,
  type ReadState,
  type Relationship,
  type Role,
  type SelfUser,
  type User,
  type VoiceState,
  type CallState,
  type GuildEmoji,
  type GuildEvent,
  type Sticker,
  type StreamQuality,
} from '@comms/shared';

export interface GuildState extends Guild {
  roles: Role[];
  memberCount: number;
  emojis: GuildEmoji[];
  stickers: Sticker[];
  /** upcoming and live */
  events: GuildEvent[];
  interestedEventIds: string[];
}

export interface ChannelMessages {
  items: Message[]; // ascending by id
  hasMore: boolean;
  loaded: boolean;
}

export interface State {
  me: SelfUser | null;
  connection: 'idle' | 'connecting' | 'ready' | 'reconnecting';
  guilds: Record<string, GuildState>;
  guildOrder: string[];
  channels: Record<string, Channel>;
  members: Record<string, Record<string, Member>>;
  users: Record<string, User>;
  presences: Record<string, Presence>;
  readStates: Record<string, ReadState>;
  relationships: Record<string, Relationship>;
  messages: Record<string, ChannelMessages>;
  typing: Record<string, Record<string, number>>;
  notificationSettings: Record<string, NotificationSetting>;
  deviceLists: Record<string, GatewayEvents['DEVICE_LIST_UPDATE']['devices']>;
  /** userId -> voice state (who is in which voice channel) */
  voiceStates: Record<string, VoiceState>;
  voiceEnabled: boolean;
  /** highest screen-share quality this server allows */
  maxStreamQuality: StreamQuality;
  gifsEnabled: boolean;
  /** DM channelId -> ongoing call */
  calls: Record<string, CallState>;
  /** threads I've joined */
  joinedThreads: Record<string, true>;
}

const initial: State = {
  me: null,
  connection: 'idle',
  guilds: {},
  guildOrder: [],
  channels: {},
  members: {},
  users: {},
  presences: {},
  readStates: {},
  relationships: {},
  messages: {},
  typing: {},
  notificationSettings: {},
  deviceLists: {},
  voiceStates: {},
  voiceEnabled: false,
  maxStreamQuality: '1080p30',
  gifsEnabled: false,
  calls: {},
  joinedThreads: {},
};

export const useStore = create<State>(() => initial);
export const resetStore = () => useStore.setState(initial, true);
const set = useStore.setState;
const get = useStore.getState;

/* ------------------------------------------------------------------ helpers */

function upsertUsers(users: User[]) {
  if (users.length === 0) return;
  set((s) => {
    const next = { ...s.users };
    for (const u of users) next[u.id] = u;
    return { users: next };
  });
}

function setChannel(c: Channel) {
  set((s) => ({ channels: { ...s.channels, [c.id]: c } }));
  if (c.recipients) upsertUsers(c.recipients);
}

function removeChannel(id: string) {
  set((s) => {
    const channels = { ...s.channels };
    delete channels[id];
    // A deleted channel takes its threads with it.
    for (const c of Object.values(channels))
      if (c.parentId === id && c.threadMetadata) delete channels[c.id];
    return { channels };
  });
}

function setGuildFull(g: GatewayEvents['GUILD_CREATE']) {
  const { channels, members, roles, joinedThreadIds, ...guild } = g;
  set((s) => {
    const nextChannels = Object.fromEntries(
      Object.entries(s.channels).filter(([, c]) => c.guildId !== g.id),
    );
    for (const c of channels) nextChannels[c.id] = c;
    const joinedThreads = { ...s.joinedThreads };
    for (const id of joinedThreadIds ?? []) joinedThreads[id] = true;
    return {
      joinedThreads,
      guilds: { ...s.guilds, [g.id]: { ...guild, roles, memberCount: g.memberCount } },
      guildOrder: s.guildOrder.includes(g.id) ? s.guildOrder : [...s.guildOrder, g.id],
      channels: nextChannels,
      members: { ...s.members, [g.id]: Object.fromEntries(members.map((m) => [m.user.id, m])) },
    };
  });
  upsertUsers(members.map((m) => m.user));
}

function updateMessages(channelId: string, fn: (items: Message[]) => Message[]) {
  set((s) => {
    const cur = s.messages[channelId];
    if (!cur) return {};
    return { messages: { ...s.messages, [channelId]: { ...cur, items: fn(cur.items) } } };
  });
}

/** Show the user's own poll vote right away; the server's tally event reconciles counts. */
export function setMyPollVote(channelId: string, messageId: string, answerIds: number[]) {
  updateMessages(channelId, (items) =>
    items.map((m) => {
      if (m.id !== messageId || !m.poll) return m;
      const had = m.poll.answers.some((a) => a.me);
      return {
        ...m,
        poll: {
          ...m.poll,
          totalVoters: m.poll.totalVoters + (answerIds.length ? 1 : 0) - (had ? 1 : 0),
          answers: m.poll.answers.map((a) => {
            const me = answerIds.includes(a.id);
            return { ...a, me, count: a.count + (me ? 1 : 0) - (a.me ? 1 : 0) };
          }),
        },
      };
    }),
  );
}

export function addMessages(channelId: string, older: Message[], hasMore: boolean) {
  set((s) => {
    const cur = s.messages[channelId] ?? { items: [], hasMore: true, loaded: false };
    const seen = new Set(cur.items.map((m) => m.id));
    const merged = [...older.filter((m) => !seen.has(m.id)), ...cur.items].sort((a, b) =>
      compareSnowflakes(a.id, b.id),
    );
    return { messages: { ...s.messages, [channelId]: { items: merged, hasMore, loaded: true } } };
  });
  upsertUsers(older.map((m) => m.author));
}

/* --------------------------------------------------------- gateway reducer */

export function applyDispatch<T extends keyof GatewayEvents>(t: T, d: GatewayEvents[T]) {
  const ev = d as never;
  switch (t) {
    case 'READY': {
      const r = ev as GatewayEvents['READY'];
      resetStore();
      resetCommands();
      set({
        me: r.user,
        connection: 'ready',
        readStates: Object.fromEntries(r.readStates.map((x) => [x.channelId, x])),
        relationships: Object.fromEntries(r.relationships.map((x) => [x.id, x])),
        presences: Object.fromEntries(r.presences.map((p) => [p.userId, p])),
        notificationSettings: Object.fromEntries(
          r.notificationSettings.map((n) => [n.targetId, n]),
        ),
        voiceStates: Object.fromEntries((r.voiceStates ?? []).map((v) => [v.userId, v])),
        voiceEnabled: r.voice?.enabled ?? false,
        maxStreamQuality: r.voice?.maxStreamQuality ?? '1080p30',
        gifsEnabled: r.gifs?.enabled ?? false,
        calls: Object.fromEntries((r.calls ?? []).map((c) => [c.channelId, c])),
      });
      upsertUsers([r.user, ...r.relationships.map((x) => x.user)]);
      r.guilds.forEach(setGuildFull);
      r.privateChannels.forEach(setChannel);
      break;
    }
    case 'USER_UPDATE': {
      const u = ev as SelfUser | User;
      upsertUsers([u]);
      if (get().me?.id === u.id && 'isAdmin' in u) set({ me: u });
      set((s) => {
        const members = { ...s.members };
        for (const gid of Object.keys(members)) {
          const m = members[gid]![u.id];
          if (m) members[gid] = { ...members[gid], [u.id]: { ...m, user: u } };
        }
        return { members };
      });
      break;
    }
    case 'GUILD_CREATE':
      setGuildFull(ev);
      break;
    case 'GUILD_UPDATE': {
      const g = ev as Guild;
      set((s) =>
        s.guilds[g.id] ? { guilds: { ...s.guilds, [g.id]: { ...s.guilds[g.id]!, ...g } } } : {},
      );
      break;
    }
    case 'GUILD_DELETE': {
      const { id } = ev as { id: string };
      set((s) => {
        const guilds = { ...s.guilds };
        delete guilds[id];
        return {
          guilds,
          guildOrder: s.guildOrder.filter((x) => x !== id),
          channels: Object.fromEntries(
            Object.entries(s.channels).filter(([, c]) => c.guildId !== id),
          ),
        };
      });
      break;
    }
    case 'GUILD_EMOJIS_UPDATE':
    case 'GUILD_STICKERS_UPDATE': {
      const d = ev as { guildId: string; emojis?: GuildEmoji[]; stickers?: Sticker[] };
      set((s) => {
        const g = s.guilds[d.guildId];
        if (!g) return {};
        return {
          guilds: {
            ...s.guilds,
            [g.id]: {
              ...g,
              ...(d.emojis ? { emojis: d.emojis } : {}),
              ...(d.stickers ? { stickers: d.stickers } : {}),
            },
          },
        };
      });
      break;
    }
    case 'GUILD_EVENT_UPDATE':
    case 'GUILD_EVENT_DELETE': {
      const e = ev as GuildEvent | { guildId: string; id: string };
      const keep = 'status' in e && (e.status === 1 || e.status === 2);
      set((s) => {
        const g = s.guilds[e.guildId];
        if (!g) return {};
        const others = g.events.filter((x) => x.id !== e.id);
        const events = keep
          ? [...others, e as GuildEvent].sort((a, b) => a.startAt.localeCompare(b.startAt))
          : others;
        return { guilds: { ...s.guilds, [g.id]: { ...g, events } } };
      });
      break;
    }
    case 'GUILD_EVENT_RSVP': {
      const r = ev as GatewayEvents['GUILD_EVENT_RSVP'];
      const mine = r.userId === get().me?.id;
      set((s) => {
        const g = s.guilds[r.guildId];
        if (!g) return {};
        const events = g.events.map((x) =>
          x.id === r.eventId ? { ...x, interestedCount: r.interestedCount } : x,
        );
        const others = g.interestedEventIds.filter((id) => id !== r.eventId);
        const interestedEventIds = mine
          ? r.interested
            ? [...others, r.eventId]
            : others
          : g.interestedEventIds;
        return { guilds: { ...s.guilds, [g.id]: { ...g, events, interestedEventIds } } };
      });
      break;
    }
    case 'GUILD_ROLE_CREATE':
    case 'GUILD_ROLE_UPDATE': {
      const r = ev as Role;
      set((s) => {
        const g = s.guilds[r.guildId];
        if (!g) return {};
        const roles = [...g.roles.filter((x) => x.id !== r.id), r].sort(
          (a, b) => a.position - b.position,
        );
        return { guilds: { ...s.guilds, [g.id]: { ...g, roles } } };
      });
      break;
    }
    case 'GUILD_ROLE_DELETE': {
      const { guildId, roleId } = ev as { guildId: string; roleId: string };
      set((s) => {
        const g = s.guilds[guildId];
        return g
          ? {
              guilds: {
                ...s.guilds,
                [guildId]: { ...g, roles: g.roles.filter((r) => r.id !== roleId) },
              },
            }
          : {};
      });
      break;
    }
    case 'GUILD_MEMBER_ADD':
    case 'GUILD_MEMBER_UPDATE': {
      const m = ev as Member;
      if (m.user.bot && t === 'GUILD_MEMBER_ADD') invalidateCommands(m.guildId);
      upsertUsers([m.user]);
      set((s) => {
        const isNew = !s.members[m.guildId]?.[m.user.id];
        const g = s.guilds[m.guildId];
        return {
          members: { ...s.members, [m.guildId]: { ...s.members[m.guildId], [m.user.id]: m } },
          ...(g && isNew && t === 'GUILD_MEMBER_ADD'
            ? { guilds: { ...s.guilds, [g.id]: { ...g, memberCount: g.memberCount + 1 } } }
            : {}),
        };
      });
      break;
    }
    case 'GUILD_MEMBER_REMOVE': {
      const { guildId, userId } = ev as { guildId: string; userId: string };
      if (get().users[userId]?.bot) invalidateCommands(guildId);
      set((s) => {
        const list = { ...s.members[guildId] };
        delete list[userId];
        const g = s.guilds[guildId];
        return {
          members: { ...s.members, [guildId]: list },
          ...(g
            ? {
                guilds: {
                  ...s.guilds,
                  [guildId]: { ...g, memberCount: Math.max(0, g.memberCount - 1) },
                },
              }
            : {}),
        };
      });
      break;
    }
    case 'CHANNEL_CREATE':
    case 'CHANNEL_UPDATE':
      setChannel(ev);
      break;
    case 'CHANNEL_DELETE':
      removeChannel((ev as { id: string }).id);
      break;
    case 'MESSAGE_POLL_VOTE': {
      const v = ev as GatewayEvents['MESSAGE_POLL_VOTE'];
      const mine = v.userId !== null && v.userId === get().me?.id;
      updateMessages(v.channelId, (items) =>
        items.map((m) =>
          m.id === v.messageId && m.poll
            ? {
                ...m,
                poll: {
                  ...m.poll,
                  closed: v.closed,
                  totalVoters: v.totalVoters,
                  answers: m.poll.answers.map((a) => ({
                    ...a,
                    count: v.counts[a.id] ?? 0,
                    me: mine ? v.answerIds.includes(a.id) : a.me,
                  })),
                },
              }
            : m,
        ),
      );
      break;
    }
    case 'THREAD_MEMBER_UPDATE': {
      const { threadId, joined } = ev as GatewayEvents['THREAD_MEMBER_UPDATE'];
      set((s) => {
        const joinedThreads = { ...s.joinedThreads };
        if (joined) joinedThreads[threadId] = true;
        else delete joinedThreads[threadId];
        return { joinedThreads };
      });
      break;
    }
    case 'MESSAGE_CREATE': {
      const m = ev as Message;
      upsertUsers([m.author]);
      // Keep messages that arrive while the channel's history is still loading:
      // the unloaded bucket is merged with the history once it arrives.
      set((s) => {
        const cur = s.messages[m.channelId] ?? { items: [], hasMore: true, loaded: false };
        if (cur.items.some((x) => x.id === m.id)) return {};
        return {
          messages: { ...s.messages, [m.channelId]: { ...cur, items: [...cur.items, m] } },
        };
      });
      set((s) => {
        const c = s.channels[m.channelId];
        return c ? { channels: { ...s.channels, [c.id]: { ...c, lastMessageId: m.id } } } : {};
      });
      clearTyping(m.channelId, m.author.id);
      if (m.author.id === get().me?.id) {
        set((s) => ({
          readStates: {
            ...s.readStates,
            [m.channelId]: { channelId: m.channelId, lastReadMessageId: m.id, mentionCount: 0 },
          },
        }));
      } else if (mentionsMe(m)) {
        set((s) => {
          const rs = s.readStates[m.channelId] ?? {
            channelId: m.channelId,
            lastReadMessageId: null,
            mentionCount: 0,
          };
          return {
            readStates: {
              ...s.readStates,
              [m.channelId]: { ...rs, mentionCount: rs.mentionCount + 1 },
            },
          };
        });
      }
      break;
    }
    case 'MESSAGE_UPDATE': {
      const m = ev as Message;
      // Reactions in broadcast updates are relative to the editor; keep our own copy.
      updateMessages(m.channelId, (items) =>
        items.map((x) => (x.id === m.id ? { ...m, reactions: x.reactions } : x)),
      );
      onMessageUpdated(m.id);
      break;
    }
    case 'MESSAGE_DELETE': {
      const { id, channelId } = ev as { id: string; channelId: string };
      updateMessages(channelId, (items) => items.filter((x) => x.id !== id));
      break;
    }
    case 'MESSAGE_DELETE_BULK': {
      const { ids, channelId } = ev as { ids: string[]; channelId: string };
      const gone = new Set(ids);
      updateMessages(channelId, (items) => items.filter((x) => !gone.has(x.id)));
      break;
    }
    case 'MESSAGE_REACTION_ADD':
    case 'MESSAGE_REACTION_REMOVE': {
      const r = ev as GatewayEvents['MESSAGE_REACTION_ADD'];
      const mine = r.userId === get().me?.id;
      const add = t === 'MESSAGE_REACTION_ADD';
      updateMessages(r.channelId, (items) =>
        items.map((m) => {
          if (m.id !== r.messageId) return m;
          const existing = m.reactions.find((x) => x.emoji === r.emoji);
          let reactions = m.reactions;
          if (add) {
            reactions = existing
              ? reactions.map((x) =>
                  x.emoji === r.emoji ? { ...x, count: x.count + 1, me: x.me || mine } : x,
                )
              : [...reactions, { emoji: r.emoji, count: 1, me: mine }];
          } else if (existing) {
            reactions = reactions
              .map((x) =>
                x.emoji === r.emoji ? { ...x, count: x.count - 1, me: mine ? false : x.me } : x,
              )
              .filter((x) => x.count > 0);
          }
          return { ...m, reactions };
        }),
      );
      break;
    }
    case 'MESSAGE_ACK': {
      const rs = ev as ReadState;
      set((s) => ({ readStates: { ...s.readStates, [rs.channelId]: rs } }));
      break;
    }
    case 'TYPING_START': {
      const { channelId, userId } = ev as GatewayEvents['TYPING_START'];
      set((s) => ({
        typing: {
          ...s.typing,
          [channelId]: { ...s.typing[channelId], [userId]: Date.now() + 9000 },
        },
      }));
      break;
    }
    case 'PRESENCE_UPDATE': {
      const p = ev as Presence;
      set((s) => ({ presences: { ...s.presences, [p.userId]: p } }));
      break;
    }
    case 'RELATIONSHIP_ADD': {
      const r = ev as Relationship;
      upsertUsers([r.user]);
      set((s) => ({ relationships: { ...s.relationships, [r.id]: r } }));
      break;
    }
    case 'RELATIONSHIP_REMOVE': {
      const { id } = ev as { id: string };
      set((s) => {
        const relationships = { ...s.relationships };
        delete relationships[id];
        return { relationships };
      });
      break;
    }
    case 'NOTIFICATION_SETTINGS_UPDATE': {
      const n = ev as NotificationSetting;
      set((s) => ({ notificationSettings: { ...s.notificationSettings, [n.targetId]: n } }));
      break;
    }
    case 'VOICE_STATE_UPDATE': {
      const v = ev as VoiceState;
      set((s) => {
        const voiceStates = { ...s.voiceStates };
        if (v.channelId) voiceStates[v.userId] = v;
        else if (voiceStates[v.userId]?.guildId === v.guildId) delete voiceStates[v.userId];
        return { voiceStates };
      });
      break;
    }
    case 'CALL_UPDATE': {
      const c = ev as CallState;
      set((s) => ({ calls: { ...s.calls, [c.channelId]: c } }));
      break;
    }
    case 'CALL_DELETE': {
      const { channelId } = ev as { channelId: string };
      set((s) => {
        const calls = { ...s.calls };
        delete calls[channelId];
        return { calls };
      });
      break;
    }
    case 'DEVICE_LIST_UPDATE': {
      const { userId, devices } = ev as GatewayEvents['DEVICE_LIST_UPDATE'];
      set((s) => ({ deviceLists: { ...s.deviceLists, [userId]: devices } }));
      break;
    }
    case 'E2EE_MESSAGE_CREATE': {
      const m = ev as GatewayEvents['E2EE_MESSAGE_CREATE'];
      if (m.kind === 'application') {
        clearTyping(m.channelId, m.senderUserId);
        set((s) => {
          const c = s.channels[m.channelId];
          return c ? { channels: { ...s.channels, [c.id]: { ...c, lastMessageId: m.id } } } : {};
        });
        if (m.senderUserId === get().me?.id) {
          set((s) => ({
            readStates: {
              ...s.readStates,
              [m.channelId]: { channelId: m.channelId, lastReadMessageId: m.id, mentionCount: 0 },
            },
          }));
        }
      }
      break;
    }
    case 'APPLICATION_COMMANDS_UPDATE':
      invalidateCommands((ev as GatewayEvents['APPLICATION_COMMANDS_UPDATE']).guildId);
      break;
    case 'INTERACTION_UPDATE':
      onInteractionUpdate(ev);
      break;
    default:
      break;
  }
}

function clearTyping(channelId: string, userId: string) {
  set((s) => {
    if (!s.typing[channelId]?.[userId]) return {};
    const next = { ...s.typing[channelId] };
    delete next[userId];
    return { typing: { ...s.typing, [channelId]: next } };
  });
}

export function mentionsMe(m: Message): boolean {
  const s = get();
  const me = s.me;
  if (!me) return false;
  if (m.mentionEveryone || m.mentions.includes(me.id)) return true;
  const myRoles = m.guildId ? (s.members[m.guildId]?.[me.id]?.roles ?? []) : [];
  return m.mentionRoles.some((r) => myRoles.includes(r));
}

/* ---------------------------------------------------------------- selectors */

export type NotifyLevel = 'all' | 'mentions' | 'nothing' | 'muted';

/**
 * How a channel notifies (same rules as the server's push): muted anywhere up the chain
 * (thread, its channel, the server) silences it; otherwise the most specific level wins.
 * Servers default to @mentions only, DMs to everything. `skipOwn` gives what the channel
 * would inherit without its own setting.
 */
export function notifyLevel(s: State, channelId: string, skipOwn = false): NotifyLevel {
  const c = s.channels[channelId];
  const chain = [
    skipOwn ? null : channelId,
    c?.threadMetadata ? c.parentId : null,
    c?.guildId ?? null,
  ].filter((id): id is string => !!id);
  const settings = chain.map((id) => s.notificationSettings[id]);
  if (settings.some((n) => n?.muted)) return 'muted';
  return settings.find((n) => n?.level)?.level ?? (c?.guildId ? 'mentions' : 'all');
}

/**
 * Whether a new message should notify (pop up, ping): by the channel's level, and in
 * threads only if you're in the thread (or mentioned), like push notifications.
 */
export function wantsNotification(s: State, channelId: string, mentioned: boolean): boolean {
  const level = notifyLevel(s, channelId);
  if (level === 'muted' || level === 'nothing') return false;
  if (level === 'mentions') return mentioned;
  return mentioned || !s.channels[channelId]?.threadMetadata || !!s.joinedThreads[channelId];
}

/**
 * The app-icon badge: unread @mentions in server channels plus DMs with unread messages,
 * leaving out anything muted. The server's push payloads use the same rule.
 */
export function badgeCount(s: State): number {
  let n = 0;
  for (const c of Object.values(s.channels)) {
    if (notifyLevel(s, c.id) === 'muted') continue;
    if (c.guildId === null) n += isUnread(s, c.id) ? 1 : 0;
    else n += s.readStates[c.id]?.mentionCount ?? 0;
  }
  return n;
}

export function isUnread(s: State, channelId: string): boolean {
  const c = s.channels[channelId];
  if (!c?.lastMessageId) return false;
  const rs = s.readStates[channelId];
  if (!rs?.lastReadMessageId) return true;
  return compareSnowflakes(c.lastMessageId, rs.lastReadMessageId) > 0;
}

export function displayName(s: State, userId: string, guildId?: string | null): string {
  const nick = guildId ? s.members[guildId]?.[userId]?.nickname : null;
  const u = s.users[userId];
  return nick ?? u?.displayName ?? u?.username ?? 'Unknown';
}

export function roleColor(
  s: State,
  guildId: string | null | undefined,
  userId: string,
): string | undefined {
  if (!guildId) return undefined;
  const member = s.members[guildId]?.[userId];
  const guild = s.guilds[guildId];
  if (!member || !guild) return undefined;
  const colored = guild.roles
    .filter((r) => member.roles.includes(r.id) && r.color !== 0)
    .sort((a, b) => b.position - a.position)[0];
  return colored ? `#${colored.color.toString(16).padStart(6, '0')}` : undefined;
}
